/**
 * Masonry Grid Handler
 * Manages masonry grid layout for multicolumn sections
 */

class MasonryGrid {
  constructor() {
    this.grids = [];
    this.observerMap = new Map();
    this.init();
  }

  init() {
    // Find all masonry grids
    document.querySelectorAll('.multicolumn-list.masonry-grid').forEach((grid) => {
      this.setupGrid(grid);
      this.grids.push(grid);
    });

    // Handle dynamic sections (for Shopify editor)
    document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
    document.addEventListener('shopify:section:reorder', this.handleSectionReorder.bind(this));
    document.addEventListener('shopify:section:select', this.handleSectionSelect.bind(this));

    // Handle window resize
    window.addEventListener(
      'resize',
      this.debounce(() => {
        this.grids.forEach((grid) => this.applyLayout(grid));
      }, 250)
    );
  }

  setupGrid(grid) {
    // Store original column count
    const parentSection = grid.closest('[class*="columns_desktop_"]');
    if (parentSection) {
      const columnMatch = parentSection.className.match(/columns_desktop_(\d+)/);
      if (columnMatch) {
        const columnCount = columnMatch[1];
        grid.classList.add(`columns-${columnCount}`);
      }
    }

    // Apply initial layout
    this.applyLayout(grid);

    // Observe images for loading
    this.observeImages(grid);
  }

  observeImages(grid) {
    const images = grid.querySelectorAll('img');

    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        this.applyLayout(grid);
      });

      images.forEach((img) => {
        resizeObserver.observe(img);
      });

      this.observerMap.set(grid, resizeObserver);
    }

    // Also trigger layout after images load
    images.forEach((img) => {
      if (img.complete) {
        this.applyLayout(grid);
      } else {
        img.addEventListener('load', () => this.applyLayout(grid), { once: true });
      }
    });
  }

  applyLayout(grid) {
    // CSS columns handle the layout automatically
    // This method can be used for additional adjustments if needed
  }

  handleSectionLoad(event) {
    const section = event.detail.section;
    const grid = section.querySelector('.multicolumn-list.masonry-grid');
    if (grid && !this.grids.includes(grid)) {
      this.setupGrid(grid);
      this.grids.push(grid);
    }
  }

  handleSectionReorder(event) {
    const section = event.detail.section;
    const grid = section.querySelector('.multicolumn-list.masonry-grid');
    if (grid) {
      this.applyLayout(grid);
    }
  }

  handleSectionSelect(event) {
    const section = event.detail.section;
    const grid = section.querySelector('.multicolumn-list.masonry-grid');
    if (grid) {
      this.applyLayout(grid);
    }
  }

  debounce(fn, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  destroy() {
    this.observerMap.forEach((observer) => {
      observer.disconnect();
    });
    this.observerMap.clear();
    this.grids = [];
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.masonryGrid = new MasonryGrid();
  });
} else {
  window.masonryGrid = new MasonryGrid();
}
